import { MolsonPriceCmdCustomersApiService } from '@api/customers'
import { MachinesApiService, OpenAPI } from '@api/machines'
import {
  handleCatchError,
  QuotationStatusMapped,
  type SelectedQuote,
  StockTypes
} from '@common/utils'
import { createStore, successToast } from '@components/reusable'

import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export const QUOTES_QUERIES_KEYS = {
  QUOTE: 'quote',
  QUOTES_LIST: 'quotesList',
  QUOTE_SPECS_BY_CUSTOMER: 'quoteSpecsByCustomer',
  QUOTE_ARCHIVED_EQUIPMENTS: 'quoteArchivedEquipments',
  GET_QUOTATION_MARGIN: 'getQuotationMargin',
  QUOTATION_HISTORY: 'quotationHistory'
}

export const CUSTOMERS_QUERIES_KEYS = {
  CUSTOMERS_LIST: 'customersList',
  CUSTOMER_QUOTATIONS: 'customerQuotations'
}

export const useGetQuotesList = ({
  canViewAllQuotes
}: {
  canViewAllQuotes: boolean
}) => {
  const url = canViewAllQuotes
    ? `${OpenAPI.BASE}/QuotationMetadata/Table`
    : `${OpenAPI.BASE}/QuotationMetadata/My/Table`

  return useSuspenseQuery({
    queryKey: [QUOTES_QUERIES_KEYS.QUOTES_LIST, canViewAllQuotes],
    queryFn: async () => {
      try {
        return createStore({
          loadUrl: url,
          onBeforeSend(_, ajaxSettings) {
            ajaxSettings.headers = {
              // biome-ignore lint/style/useNamingConvention: <explanation>
              Authorization: `Bearer ${OpenAPI.TOKEN}`
            }
          },
          onAjaxError(e) {
            handleCatchError(e.xhr)
          }
        })
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 60 * 60 * 1000 // 60 minutes
  })
}

export const useGetSpecsByCustomer = (customerId: number) => {
  return useSuspenseQuery({
    queryKey: [QUOTES_QUERIES_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId],
    queryFn: async () => {
      try {
        return await MachinesApiService.customersGetAllSpecsByCustomer(
          customerId
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 60 * 60 * 1000,
    refetchOnMount: true
  })
}

export const useGetCustomers = ({
  page,
  itemsPerPage,
  searchTerm
}: {
  page: number
  itemsPerPage: number
  searchTerm: string
}) => {
  return useQuery({
    queryKey: [CUSTOMERS_QUERIES_KEYS.CUSTOMERS_LIST, searchTerm],
    queryFn: async () => {
      try {
        return await MolsonPriceCmdCustomersApiService.customersPage(
          page,
          itemsPerPage,
          searchTerm
        )
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 30 * 60 * 1000,
    throwOnError: false
  })
}

export const useGetCustomerQuotations = ({
  customerId,
  isNewQuote
}: {
  customerId: number
  isNewQuote: boolean
}) => {
  return useQuery({
    queryKey: [CUSTOMERS_QUERIES_KEYS.CUSTOMER_QUOTATIONS, customerId],
    queryFn: async () => {
      try {
        return await MachinesApiService.allQuotationsByCustomer(customerId)
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 30 * 60 * 1000,
    throwOnError: false,
    enabled: !isNewQuote
  })
}

export const useCreateQuote = () => {
  return useMutation({
    mutationFn: async ({
      quotationId,
      customerId,
      quotationName,
      customerContactId,
      deliveryAddressId
    }: {
      quotationId: string
      customerId: string
      quotationName: string
      customerContactId?: number
      deliveryAddressId?: number
    }) => {
      return await MachinesApiService.createQuotation(
        quotationId,
        customerId,
        quotationName,
        customerContactId,
        deliveryAddressId
      )
    },
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: () => {
      successToast()
    }
  })
}

export const useCreateQuoteOnBehalf = () => {
  return useMutation({
    mutationFn: async ({
      quotationId,
      customerId,
      userName,
      quotationName,
      customerContactId,
      deliveryAddressId
    }: {
      quotationId: string
      customerId: string
      userName: string
      quotationName: string
      customerContactId?: number
      deliveryAddressId?: number
    }) => {
      return await MachinesApiService.createQuotationBehalfOn(
        quotationId,
        customerId,
        userName,
        quotationName,
        customerContactId,
        deliveryAddressId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()
    },
    onError: error => handleCatchError(error)
  })
}

export const useAddSpecToQuotation = () => {
  const { t } = useTranslation('', {
    keyPrefix: 'specs.grid.actions.addToQuote'
  })

  return useMutation({
    mutationFn: async ({
      specId,
      customerId,
      quotationId
    }: {
      specId: string
      customerId: number
      quotationId: string
    }) => {
      return await MachinesApiService.specAddSpecToQuotation(
        specId,
        customerId,
        quotationId
      )
    },
    onError: error => {
      handleCatchError(error)
    },
    throwOnError: false,
    onSuccess() {
      successToast(`${t('specAddedToQuote')}`)
    }
  })
}

export const useOnAddAttachmentWithinMachineToQuotation = ({
  quotationId
}: {
  quotationId?: string
}) => {
  return useMutation({
    mutationFn: async ({
      machineId,
      selectedAttachmentId,
      stockType
    }: {
      machineId: string
      selectedAttachmentId: number
      stockType: string
    }) => {
      if (quotationId === undefined) {
        return null
      }

      const addEndpointByStockType =
        stockType === StockTypes.FACTORY_ORDER
          ? MachinesApiService.quotationMachineAddMachineAttachment
          : MachinesApiService.quotationMachineAddMachineAttachmentStock

      return await addEndpointByStockType(
        quotationId,
        machineId,
        selectedAttachmentId
      )
    },
    throwOnError: false,
    onError: error => {
      handleCatchError(error)
    },
    onSuccess: () => {
      successToast()
    }
  })
}

export const useGetQuotationMargin = (quoteId?: string) => {
  return useSuspenseQuery({
    queryKey: [QUOTES_QUERIES_KEYS.GET_QUOTATION_MARGIN, quoteId],
    queryFn: async () => {
      try {
        if (quoteId) {
          return await MachinesApiService.quotationMargin(quoteId)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    refetchOnMount: true,
    staleTime: 0
  })
}

export const useGetQuotationHistory = ({ quoteId }: { quoteId?: string }) => {
  return useSuspenseQuery({
    queryKey: [QUOTES_QUERIES_KEYS.QUOTATION_HISTORY, quoteId],
    queryFn: async () => {
      try {
        if (quoteId) {
          return await MachinesApiService.quotationHistoryId(quoteId)
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    staleTime: 60 * 60 * 1000, // 60 minutes
    refetchOnMount: 'always'
  })
}

const invalidateQuoteQueries = (queryClient: QueryClient, quoteId: string) => {
  queryClient.invalidateQueries({
    queryKey: [QUOTES_QUERIES_KEYS.QUOTE, quoteId]
  })

  queryClient.invalidateQueries({
    queryKey: [QUOTES_QUERIES_KEYS.GET_QUOTATION_MARGIN, quoteId]
  })
}

export const useArchiveQuote = ({
  selectedQuotes,
  isFromQuotePage = false
}: {
  selectedQuotes: SelectedQuote[]
  isFromQuotePage?: boolean
}) => {
  const isSingleQuote = selectedQuotes.length === 1

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quotesIds }: { quotesIds: string[] }) => {
      if (!quotesIds || quotesIds.length === 0) {
        return
      }
      return await MachinesApiService.quotationUpdateStatus(
        QuotationStatusMapped.Archived,
        quotesIds
      )
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      if (isFromQuotePage) {
        successToast(`${t('archiveQuoteSuccessMessage')}`)
      } else if (isSingleQuote) {
        const quoteName = selectedQuotes[0]?.quotationName
        successToast(`${t('successfullyArchivedQuote', { quoteName })}`)
      } else {
        const quotesCount = selectedQuotes.length
        successToast(`${t('successfullyArchivedQuotes', { quotesCount })}`)
      }

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QUOTES_QUERIES_KEYS.QUOTES_LIST]
        })
      }, 700)

      if (isFromQuotePage) {
        invalidateQuoteQueries(queryClient, selectedQuotes[0].id)
      }
    }
  })
}

export const useRestoreQuoteToDraft = ({
  selectedQuotes,
  isFromQuotePage = false
}: {
  selectedQuotes: SelectedQuote[]
  isFromQuotePage?: boolean
}) => {
  const isSingleQuote = selectedQuotes.length === 1

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quotesIds }: { quotesIds: string[] }) => {
      if (!quotesIds || quotesIds.length === 0) {
        return
      }
      return await MachinesApiService.quotationReturnToDraft(quotesIds)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      if (isFromQuotePage) {
        successToast(`${t('quoteRestoredSuccess')}`)
      } else if (isSingleQuote) {
        const quoteName = selectedQuotes[0]?.quotationName
        successToast(`${t('successfullyRestoredQuote', { quoteName })}`)
      } else {
        const quotesCount = selectedQuotes.length
        successToast(`${t('successfullyRestoredQuotes', { quotesCount })}`)
      }

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QUOTES_QUERIES_KEYS.QUOTES_LIST]
        })
      }, 700)

      if (isFromQuotePage) {
        invalidateQuoteQueries(queryClient, selectedQuotes[0].id)
      }
    }
  })
}

export const useDeleteQuote = ({
  selectedQuotes
}: {
  selectedQuotes: SelectedQuote[]
}) => {
  const isSingleQuote = selectedQuotes.length === 1

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ quotesIds }: { quotesIds: string[] }) => {
      if (!quotesIds || quotesIds.length === 0) {
        return
      }
      return await MachinesApiService.quotationsDelete(quotesIds)
    },
    throwOnError: false,
    onError: error => handleCatchError(error),
    onSuccess: () => {
      if (isSingleQuote) {
        const quoteName = selectedQuotes[0]?.quotationName
        successToast(`${t('successfullyDeletedQuote', { quoteName })}`)
      } else {
        const quotesCount = selectedQuotes.length
        successToast(`${t('successfullyDeletedQuotes', { quotesCount })}`)
      }

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QUOTES_QUERIES_KEYS.QUOTES_LIST]
        })
      }, 700)

      if (isSingleQuote) {
        invalidateQuoteQueries(queryClient, selectedQuotes[0].id)
      }
    }
  })
}
